import { useEffect, Suspense } from "react";
import { useAuthStore } from "@/store/useAuthStore";
import { useNavigate, useLocation } from "react-router";
import { UserRole } from "@/constants";
import GTMService from "@/services/GTMService";
import Cookie from "js-cookie";
import { safeSessionGetItem } from "@/utils/safeStorage";

function AuthProviderContent({ children }: { children: React.ReactNode }) {
  const isLoggedIn = useAuthStore((state) => state.isLoggedIn);
  const setToken = useAuthStore((state) => state.setToken);
  const logoutUser = useAuthStore((state) => state.logoutUser);
  // const token = useAuthStore((state) => state.token);
  const organizationId = useAuthStore((state) => state.organizationId);
  const role = useAuthStore((state) => state.role);
  const initCompleted = useAuthStore((state) => state.initCompleted);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const storeToken = (token: string) => {
      Cookie.set("accessToken", token, { expires: 0.25, path: "/" }); // Set cookie with 6 hours expiry
    };

    // Token is already extracted in index.html BEFORE React loads
    // Try multiple sources: sessionStorage → localStorage → memory fallback
    let token = safeSessionGetItem("accessToken");

    if (
      !token &&
      typeof localStorage !== "undefined" &&
      localStorage !== null
    ) {
      try {
        token = localStorage.getItem("token");
      } catch (e) {
        console.warn("Failed to read from localStorage:", e);
      }
    }

    // Fallback to memory if storage is unavailable (WebView without DOM storage)
    if (
      !token &&
      typeof window !== "undefined" &&
      (window as any).__marketbuzz_token__
    ) {
      token = (window as any).__marketbuzz_token__;
      console.log("[Auth] Using memory fallback token");
    }

    if (token) {
      setToken(token);
      storeToken(token);
    } else {
      logoutUser().catch(console.error);
    }
  }, [setToken, logoutUser]);

  useEffect(() => {
    if (initCompleted) {
      // Skip authentication check for auth flow pages
      if (
        location.pathname.startsWith("/auth/clover/app-selection") ||
        location.pathname === "/auth/error"
      ) {
        return;
      }

      // Add small delay to ensure token persistence completes (Zustand async storage)
      const checkAuth = setTimeout(() => {
        if (!isLoggedIn()) {
          navigate("/login");
        } else if (!organizationId && role === UserRole.SUPERADMIN) {
          navigate("/admin/switch");
          GTMService.loginEvent({ organizationId, role });
        } else {
          GTMService.loginEvent({ organizationId, role });
        }
      }, 100);

      return () => clearTimeout(checkAuth);
    }
  }, [
    isLoggedIn,
    navigate,
    organizationId,
    role,
    initCompleted,
    location.pathname
  ]);

  return <>{children}</>;
}

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <AuthProviderContent>{children}</AuthProviderContent>
    </Suspense>
  );
};

export default AuthProvider;
